import { ApiError } from '@commissionsight/sdk';

/**
 * Saved/custom reports — app-only (not in the public SDK). Save a book answer's
 * query as a named report, then re-run it deterministically.
 */
async function call<T>(
  baseUrl: string,
  token: string | null,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'title' in body
        ? String((body as { title: unknown }).title)
        : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export type Cadence = 'weekly' | 'monthly' | null;

export interface SavedReport {
  id: string;
  name: string;
  question: string | null;
  createdAt: number;
  lastRunAt: number | null;
  cadence: Cadence;
}

export interface ReportRun {
  id: string;
  name: string;
  columns: string[];
  rows: Record<string, unknown>[];
  truncated: boolean;
}

export function listSavedReports(
  baseUrl: string,
  token: string | null,
): Promise<{ reports: SavedReport[] }> {
  return call(baseUrl, token, '/reports/saved');
}

export function saveReport(
  baseUrl: string,
  token: string | null,
  input: { name: string; question?: string; sql: string },
): Promise<{ id: string; name: string }> {
  return call(baseUrl, token, '/reports/saved', { method: 'POST', body: JSON.stringify(input) });
}

export function runSavedReport(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<ReportRun> {
  return call(baseUrl, token, `/reports/saved/${id}/run`, { method: 'POST' });
}

export function deleteSavedReport(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<{ id: string; deleted: boolean }> {
  return call(baseUrl, token, `/reports/saved/${id}`, { method: 'DELETE' });
}

export function scheduleReport(
  baseUrl: string,
  token: string | null,
  id: string,
  cadence: 'off' | 'weekly' | 'monthly',
): Promise<{ id: string; cadence: Cadence }> {
  return call(baseUrl, token, `/reports/saved/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ cadence }),
  });
}
